import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  File, 
  X, 
  Shield, 
  Lock, 
  CheckCircle, 
  AlertTriangle,
  Loader2
} from 'lucide-react';
import Card from '../atoms/Card';
import Button from '../atoms/Button';
import { validateFileSignature } from '../../lib/encryption';

interface SecureUploadZoneProps {
  onFilesSelected: (files: File[]) => void;
  maxFiles?: number;
  maxSize?: number;
  acceptedFileTypes?: string[];
  className?: string;
  isUploading?: boolean;
  uploadProgress?: number;
}

interface FileWithValidation {
  id: string;
  originalFile: File;
  isValid?: boolean;
  validationError?: string;
}

const SecureUploadZone: React.FC<SecureUploadZoneProps> = ({
  onFilesSelected,
  maxFiles = 10,
  maxSize = 100 * 1024 * 1024, // 100MB for Pinata paid tier
  acceptedFileTypes = [
    'application/pdf',
    'image/*',
    'text/*',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ],
  className = '',
  isUploading = false,
  uploadProgress = 0
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithValidation[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  const formatFileSize = (bytes: number): string => {
    // Handle invalid inputs
    if (!bytes || bytes === 0 || isNaN(bytes) || !isFinite(bytes)) {
      return '0 B';
    }
    
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    
    // Ensure we don't go out of bounds
    const sizeIndex = Math.min(i, sizes.length - 1);
    const size = bytes / Math.pow(1024, sizeIndex);
    
    // Round to 2 decimal places and ensure we have a valid size unit
    return `${Math.round(size * 100) / 100} ${sizes[sizeIndex] || 'B'}`;
  };

  const validateFiles = async (files: File[]): Promise<FileWithValidation[]> => {
    setIsValidating(true);
    
    const validatedFiles: FileWithValidation[] = [];
    
    for (const file of files) {
      const fileWithValidation: FileWithValidation = {
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        originalFile: file
      };

      try {
        // Validate file size first
        if (!file.size || file.size <= 0) {
          fileWithValidation.isValid = false;
          fileWithValidation.validationError = 'Invalid file size';
        } else if (file.size > maxSize) {
          fileWithValidation.isValid = false;
          fileWithValidation.validationError = `File too large (max ${formatFileSize(maxSize)})`;
        } else {
          // Validate file signature
          const isValidSignature = await validateFileSignature(file);
          
          if (!isValidSignature) {
            fileWithValidation.isValid = false;
            fileWithValidation.validationError = 'Invalid file signature - file may be corrupted or malicious';
          } else {
            fileWithValidation.isValid = true;
          }
        }
      } catch (error) {
        fileWithValidation.isValid = false;
        fileWithValidation.validationError = 'Failed to validate file';
      }

      validatedFiles.push(fileWithValidation);
    }
    
    setIsValidating(false);
    return validatedFiles;
  };

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
    if (acceptedFiles.length > 0) {
      const validatedFiles = await validateFiles(acceptedFiles);
      setSelectedFiles(prev => [...prev, ...validatedFiles]);
    }
  }, [maxSize]);

  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    onDrop,
    maxFiles,
    maxSize,
    accept: acceptedFileTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {} as Record<string, string[]>),
    disabled: isUploading
  });

  const removeFile = (fileId: string) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleUpload = () => {
    const validFiles = selectedFiles.filter(f => f.isValid);
    if (validFiles.length > 0) {
      // Extract the original File objects from FileWithValidation
      const originalFiles = validFiles.map(f => f.originalFile);
      onFilesSelected(originalFiles);
    }
  };

  const validFiles = selectedFiles.filter(f => f.isValid);
  const invalidFiles = selectedFiles.filter(f => !f.isValid);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Drop Zone */}
      <motion.div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300
          ${isDragActive && !isDragReject 
            ? 'border-primary-500 bg-primary-50' 
            : isDragReject
            ? 'border-red-500 bg-red-50'
            : isUploading
            ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
          }
        `}
        whileHover={!isUploading ? { scale: 1.01 } : {}}
        whileTap={!isUploading ? { scale: 0.99 } : {}}
      >
        <input {...getInputProps()} />
        
        <div className="space-y-4">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-300 ${
            isDragActive ? 'bg-primary-100' : 'bg-gray-100'
          }`}>
            {isValidating ? (
              <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
            ) : isUploading ? (
              <Upload className="h-8 w-8 text-gray-400" />
            ) : (
              <Upload className={`h-8 w-8 ${isDragActive ? 'text-primary-600' : 'text-gray-500'}`} />
            )}
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {isUploading 
                ? 'Uploading to Pinata IPFS...' 
                : isDragActive 
                  ? isDragReject 
                    ? 'Invalid file type' 
                    : 'Drop files here'
                  : 'Upload secure documents'
              }
            </h3>
            
            <p className="text-sm text-gray-600">
              {isUploading 
                ? `Upload progress: ${uploadProgress}%`
                : 'Drag and drop files here, or click to browse'
              }
            </p>
            
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Shield size={12} />
                <span>AES-256 Encrypted</span>
              </div>
              <div className="flex items-center gap-1">
                <Lock size={12} />
                <span>Pinata IPFS</span>
              </div>
            </div>
            
            <p className="text-xs text-gray-500">
              Max {maxFiles} files, up to {formatFileSize(maxSize)} each
            </p>
          </div>
          
          {!isUploading && (
            <Button variant="outline" size="sm" disabled={isValidating}>
              {isValidating ? 'Validating...' : 'Browse Files'}
            </Button>
          )}
        </div>

        {/* Upload Progress Bar */}
        {isUploading && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-xl overflow-hidden">
            <motion.div
              className="h-full bg-primary-500"
              initial={{ width: 0 }}
              animate={{ width: `${uploadProgress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        )}
      </motion.div>

      {/* File Rejections */}
      {fileRejections.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <h4 className="font-medium text-red-800">Some files were rejected:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {fileRejections.map(({ file, errors }, index) => (
                  <li key={`rejection-${index}-${file.name}`} className="flex items-center gap-2">
                    <File size={14} />
                    <span className="truncate">{file.name}</span>
                    <span className="text-xs">({errors[0]?.message})</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Selected Files */}
      <AnimatePresence>
        {selectedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            <h4 className="font-medium text-gray-900">Selected Files ({selectedFiles.length})</h4>
            
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {selectedFiles.map((fileWithValidation) => (
                <motion.div
                  key={fileWithValidation.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${
                    fileWithValidation.isValid 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    fileWithValidation.isValid ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {fileWithValidation.isValid ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileWithValidation.originalFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(fileWithValidation.originalFile.size)}
                    </p>
                    {fileWithValidation.validationError && (
                      <p className="text-xs text-red-600 mt-1">
                        {fileWithValidation.validationError}
                      </p>
                    )}
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(fileWithValidation.id)}
                    disabled={isUploading}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </Button>
                </motion.div>
              ))}
            </div>

            {/* Upload Button */}
            {validFiles.length > 0 && !isUploading && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  {validFiles.length} valid file{validFiles.length !== 1 ? 's' : ''} ready to upload
                  {invalidFiles.length > 0 && (
                    <span className="text-red-600 ml-2">
                      ({invalidFiles.length} invalid)
                    </span>
                  )}
                </div>
                
                <Button
                  variant="primary"
                  onClick={handleUpload}
                  icon={Shield}
                  disabled={validFiles.length === 0}
                >
                  Encrypt & Upload to Pinata
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SecureUploadZone;